import { useJsApiLoader } from '@react-google-maps/api';
import React from 'react';

export const MapInfoContext = React.createContext(null);

export const getMousePositionFromEvent = (e) => ({
  lat: e.latLng.lat(),
  lng: e.latLng.lng(),
});

const libraries = ['places', 'drawing', 'geometry', 'marker'];

const MapInfoProvider = (props) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const [mapInstance, setMapInstance] = React.useState(null);
  const [mapBounds, setMapBounds] = React.useState(null);

  const overlayViewsRef = React.useRef(null); // Use a ref to get around the memoized stuff
  const [overlayViews, setOverlayViews] = React.useState([]);

  React.useEffect(() => {
    overlayViewsRef.current = overlayViews;
  }, [overlayViews]);

  const addOverlayView = (key, priority, render, position = null) => {
    setOverlayViews((currentOverlayViews) => {
      const newOverlayViews = [...currentOverlayViews].filter((o) => o.key !== key);

      newOverlayViews.push({
        key,
        priority,
        render,
        position,
      });

      newOverlayViews.sort((a, b) => {
        if (a.priority < b.priority) return -1;
        return 1;
      });

      overlayViewsRef.current = newOverlayViews;
      return newOverlayViews;
    });
  };

  const removeOverlayView = (key) => {
    setOverlayViews((currentOverlayViews) => {
      const newOverlayViews = [...currentOverlayViews].filter((o) => o.key !== key);
      overlayViewsRef.current = newOverlayViews;
      return newOverlayViews;
    });
  };

  const MapInfoProviderValue = React.useMemo(() => ({
    isMapsApiLoaded: isLoaded,
    mapInstance,
    setMapInstance,
    mapBounds,
    setMapBounds,
    overlayViews,
    addOverlayView,
    removeOverlayView,
  }), [isLoaded, mapInstance, mapBounds, overlayViews]);

  return <MapInfoContext.Provider value={MapInfoProviderValue} {...props} />;
};

export const useMapInfoContext = () => React.useContext(MapInfoContext);

export const withMapInfoProvider = (Component) => ({ ...props }) => (
  <MapInfoProvider>
    <Component {...props} />
  </MapInfoProvider>
);

export default MapInfoProvider;
